import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import DataTable from "react-data-table-component";

/* MATERIAL TAILWIND COMPONENTS */
import { Button } from "@material-tailwind/react";

/* ICONS */
import { TbFileInvoice } from "react-icons/tb";

/* SAMPLE DATA */
import inventory from "../../../../data/inventory";
import { BiCheck } from "react-icons/bi";
import TextArea from "../../../../components/textarea";
import { Link, useNavigate, useParams } from "react-router-dom";

import {
  getRegistrationDetails,
  patchRegistrationDetailsSendEmailById,
  patchRegistrationDetailsStatusById,
} from "../../../../service/api/auth.api";
import { toast } from "react-toast";
import { IMAGE_URL } from "../../../../config";
import moment from "moment/moment";
import { useSelector } from "react-redux";
import { handleAddToken } from "../../../../service/request";

const VendorDetails = () => {
  // GLOBAL VARIABLES
  const { id } = useParams();
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state?.user);
  // STATE VARIABLES
  const [loading, setLoading] = useState(false);
  const [registrationDetails, setRegistrationDetails] = useState(null);
  const [textContent, setContent] = useState(null);

  /* HANDLE DELETE DATA */
  const handleGetProductDetails = async () => {
    setLoading(true);
    try {
      await handleAddToken(userDetails?.token);
      const res = await getRegistrationDetails(id);
      toast.success(res?.message);
      setRegistrationDetails(res?.data);
    } catch (error) {
      console.log("%c Line:52 🍿 error", "color:#fca650", error);
      toast.error(error?.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveVendorFunction = async () => {
    setLoading(true);
    try {
      await handleAddToken(userDetails?.token);

      const res = await patchRegistrationDetailsStatusById(id, {
        status: "approved",
      });
      toast.success(res?.message);
    } catch (error) {
      toast.error(error?.response?.data?.message || "An error occurred");
    } finally {
      setLoading(false);
      await handleGetProductDetails();
    }
  };

  const handleSendEmailToVendorFunction = async () => {
    if (textContent) {
      setLoading(true);
      try {
        await handleAddToken(userDetails?.token);
        setContent(null);
        const res = await patchRegistrationDetailsSendEmailById(id, {
          content: textContent,
        });
        toast.success(res?.message);
      } catch (error) {
        toast.error(error?.response?.data?.message || "An error occurred");
      } finally {
        setLoading(false);
        setContent(null);
        await handleGetProductDetails();
      }
    } else {
      toast.error(e?.response?.data?.message || "An error occurred");
    }
  };

  useEffect(() => {
    handleGetProductDetails();
  }, [id]);

  return (
    <section className="px-0 py-2 xl:p-10 lg:p-10 min-h-screen xl:min-h-full lg:min-h-full">
      {/* PAGE TITLE */}
      <Helmet>
        <title>Vendor Details | CIDA Perfumes</title>
      </Helmet>

      <div className="container-full-width">
        {registrationDetails === null && loading === true && (
          <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5 mt-3">
            <p className="text-base xl:text-lg lg:text-lg font-proxima-regular text-greyText tracking-tight text-center">
              Please wait we are fetching your registration details...
            </p>
          </div>
        )}
        {registrationDetails === null && loading === false && (
          <div className="w-full bg-[#F5F5F5] bg-opacity-50 rounded-md border border-authBorder py-5 mt-3">
            <p className="text-base xl:text-lg lg:text-lg font-proxima-regular text-greyText tracking-tight text-center">
              We were not able to find registration details.
            </p>
          </div>
        )}
        <div className="block xl:flex lg:flex items-center justify-between">
          {/* HEADING */}
          <div>
            <h1 className="text-3xl font-proxima-bold text-black tracking-tight">
              Vendor Details
            </h1>
            <h2 className="text-lg font-proxima-regular text-greyText tracking-tight">
              Registration details have been forwarded to{" "}
              <span className="font-proxima-bold">
                {registrationDetails?.email}
              </span>
            </h2>
          </div>

          {/* VIEW INVOICE */}
          <div className="flex items-center gap-2 mt-2 xl:mt-0 lg:mt-0">
            <Button
              onClick={() => {
                navigate("/vendors");
              }}
              className="w-full xl:w-auto lg:w-auto whitespace-nowrap xl:whitespace-normal lg:whitespace-normal shadow-none hover:shadow-none bg-white hover:bg-black hover:bg-opacity-5 px-5 py-3 rounded-md border border-authBorder text-sm font-proxima-semibold text-black normal-case transition-all duration-300 ease-in-out"
            >
              Back to Vendors
            </Button>
            {registrationDetails?.status !== "approved" && (
              <Button
                onClick={() => {
                  handleApproveVendorFunction();
                }}
                className="w-full xl:w-auto lg:w-auto whitespace-nowrap xl:whitespace-normal lg:whitespace-normal justify-center shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md border border-logoYellow text-sm font-proxima-semibold text-white normal-case transition-all duration-300 ease-in-out flex"
              >
                <BiCheck className="w-5 h-5 mr-1" /> Approve the account
              </Button>
            )}
          </div>
        </div>

        {/* CUSTOMERS LIST */}
        <div className="mt-5 xl:mt-5 lg:mt-5">
          <div className=" bg-white border border-authBorder rounded-md overflow-y-auto overflow-x-hidden hide-scrollbar">
            <div className="grid grid-cols-1 xl:grid-cols-4 lg:grid-cols-4 h-full">
              <div className="col-span-3 h-full order-2 xl:order-1 lg:order-1">
                <div className="border-r border-authBorder h-full">
                  <div className="p-5 xl:p-5 lg:p-5">
                    <div className="grid grid-cols-12 h-full gap-5 pb-3 ">
                      {/* SHIPPING INFORMATION */}
                      <div className="col-span-12 lg:col-span-6 px-3 py-3 ">
                        <h2 className="text-xl font-proxima-bold text-black">
                          Web Account Information
                        </h2>
                        <h3 className="text-base font-proxima-regular text-greyText border-b border-authBorder pb-2">
                          Web account information, including personal details,
                          and login credentials
                        </h3>

                        <p className="text-base font-proxima-regular text-greyText mt-3">
                          {registrationDetails?.firstName}{" "}
                          {registrationDetails?.lastName}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText">
                          {registrationDetails?.email}
                        </p>

                        <h3 className="text-xl font-proxima-bold text-black border-b border-authBorder pb-2 mt-5">
                          Documents
                        </h3>

                        <p className="text-base font-proxima-regular text-greyText mt-2">
                          <span className="text-black font-proxima-bold">
                            GST Identification Number :
                          </span>{" "}
                          {console.log(
                            "FILE",
                            IMAGE_URL +
                              registrationDetails?.attachedDocument?.find(
                                (el) => el.documentName === "PAN Card Number"
                              )?.documentURL
                          )}
                          {registrationDetails?.gstNumber}{" "}
                          <Link
                            to={
                              IMAGE_URL +
                              registrationDetails?.attachedDocument?.find(
                                (el) =>
                                  el.documentName ===
                                  "GST Identification Number"
                              )?.documentURL
                            }
                            target="_blank"
                          >
                            <span className="text-black font-proxima-bold">
                              ( View File )
                            </span>
                          </Link>
                        </p>
                        <p className="text-base font-proxima-regular text-greyText mt-2">
                          <span className="text-black font-proxima-bold">
                            PAN Card Number :
                          </span>{" "}
                          {registrationDetails?.panCardNumber}{" "}
                          <Link
                            to={
                              IMAGE_URL +
                              registrationDetails?.attachedDocument?.find(
                                (el) => el.documentName === "PAN Card Number"
                              )?.documentURL
                            }
                            target="_blank"
                          >
                            <span className="text-black font-proxima-bold">
                              ( View File )
                            </span>
                          </Link>
                        </p>
                      </div>

                      {/* BILLING INFORMATION */}
                      <div className="col-span-12 lg:col-span-6 px-3 py-3">
                        <h2 className="text-xl font-proxima-bold text-black">
                          Contact Information
                        </h2>
                        <h3 className="text-base font-proxima-regular text-greyText border-b border-authBorder pb-2">
                          Essential contact details, including address and
                          contact information.
                        </h3>

                        <p className="text-base font-proxima-regular text-greyText mt-3">
                          {registrationDetails?.firstName}{" "}
                          {registrationDetails?.lastName}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText">
                          {registrationDetails?.contactInformation?.email}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText">
                          {registrationDetails?.contactInformation?.phoneNumber}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText mt-2">
                          {registrationDetails?.address?.[0]?.addressLine1},{" "}
                          {registrationDetails?.address?.[0]?.addressLine2}
                        </p>
                        <p className="text-base font-proxima-regular text-greyText ">
                          {registrationDetails?.address?.[0]?.city},{" "}
                          {registrationDetails?.address?.[0]?.state},{" "}
                          {registrationDetails?.address?.[0]?.zipCode} -{" "}
                          {registrationDetails?.address?.[0]?.country}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="col-span-1 h-full order-1 xl:order-2 lg:order-2">
                <div className="p-5 xl:p-5 lg:p-5">
                  <div className="grid grid-cols-8 border-b border-authBorder px-0 py-5 xl:px-2 lg:px-2">
                    <div className="col-span-5">
                      <h2 className="text-xl font-proxima-bold text-black">
                        Vendor ID #{registrationDetails?.vendorId}
                      </h2>
                      <h3 className=" text-sm font-proxima-regular text-greyText  pb-2">
                        {moment(registrationDetails?.createdAt).format(
                          "DD MMMM YYYY, hh:mm A"
                        )}
                      </h3>
                    </div>
                    <div className="col-span-3 mt-3">
                      {registrationDetails?.status === "notApproved" ? (
                        <span class="bg-red-100  text-red-800 text-sm font-medium me-2 px-2.5 py-2 rounded dark:bg-red-900 dark:text-red-300">
                          Not Approved
                        </span>
                      ) : registrationDetails?.status === "approved" ? (
                        <span class="bg-green-100  text-green-800 text-sm font-medium me-2 px-2.5 py-2 rounded dark:bg-green-900 dark:text-green-300">
                          Approved
                        </span>
                      ) : (
                        <span class="bg-orange-100  text-orange-800 text-sm font-medium me-2 px-2.5 py-2 rounded dark:bg-orange-900 dark:text-orange-300">
                          Pending
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-3">
                    <div className="bg-greyBg rounded-md w-full py-2 px-5">
                      <p className="text-sm font-proxima-semibold text-greyText">
                        Provide your details or feedback by sending an email or
                        writing a comment below.
                      </p>
                    </div>

                    {/* GRAND TOTAL */}
                    <div className="grid grid-cols-2 py-2">
                      <div className="col-span-2 w-full">
                        <TextArea
                          placeholder="Write an comment"
                          name="search"
                          id="search"
                          value={textContent}
                          onChange={(e) => setContent(e?.target?.value)}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="mt-3">
                    <Button
                      onClick={() => {
                        handleSendEmailToVendorFunction();
                      }}
                      className="w-full xl:w-auto lg:w-auto whitespace-nowrap xl:whitespace-normal lg:whitespace-normal justify-center shadow-none hover:shadow-none bg-logoYellow hover:bg-opacity-80 px-5 py-3 rounded-md border border-logoYellow text-sm font-proxima-semibold text-white normal-case transition-all duration-300 ease-in-out flex"
                    >
                      Send Email
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
export default VendorDetails;
